// The builder's form as the request both doors take: the generated
// `CreateTemplateRequest`. The ONE forward mapping — the trader app and the
// firm console send exactly this, each to its own door.
//
// A rule the author has not finished is never sent: the mapping throws the
// refusal the API would answer with, in the same words.

import type { CustomChallengeBuilderInput, HedgeMarginMethod, PositionMode } from '../form';
import type { CreateRuleRequest, CreateStepRequest, CreateTemplateRequest, ServedRuleScope, SettlementMode, SymbolGroupPin } from '../wire';
import { NO_LIVE_LIMIT, failedAccountEnd, practicePassSettings } from './builderToTemplate';
import { ruleAuthoringRefusal, toLadder, toRuleParams } from './ruleParamsWire';
import { needsCalendarSource, type RuleScope } from './ruleRegistry';
import { requestToBuilder, sameTerms } from './requestToBuilder';

/** How every template the builder writes settles: everything authored here is
 *  a simulation, so its payouts read as simulated ones. */
export const TEMPLATE_SETTLEMENT: SettlementMode = 'simulated';

/** The executable form of a bucket: a group sends its NAME and revision, never
 *  the membership the author typed beside it. */
export function wireScope(scope: RuleScope): ServedRuleScope {
  return scope.kind === 'symbol_group'
    ? { kind: 'symbol_group', value: { id: scope.value.id, revision: scope.value.revision } }
    : scope;
}

/** Every group any enabled rule names, with the membership the author stated.
 *
 *  Merged by id: two rows may measure the same group, and a group has one
 *  membership per revision — the union of what was typed, canonically ordered,
 *  is what the platform pins. */
export function authoredSymbolGroups(payload: CustomChallengeBuilderInput): SymbolGroupPin[] {
  const byId = new Map<string, { id: string; revision: number; members: Set<string> }>();
  for (const step of payload.steps) {
    for (const rule of step.rules) {
      if (!rule.enabled || rule.scope.kind !== 'symbol_group') continue;
      const { id, revision, members } = rule.scope.value;
      if (!id) continue;
      const key = `${id}@${revision}`;
      const existing = byId.get(key) ?? { id, revision, members: new Set<string>() };
      for (const member of members) existing.members.add(member);
      byId.set(key, existing);
    }
  }
  return [...byId.values()]
    .map((group) => ({ id: group.id, revision: group.revision, members: [...group.members].sort() }))
    .sort((left, right) => (left.id === right.id ? left.revision - right.revision : left.id < right.id ? -1 : 1));
}

function executionProfileKey(
  leverage: string,
  config: { positionMode: PositionMode; hedgeMarginMethod?: HedgeMarginMethod; hedgeOffsetFactor?: string },
): string {
  const normalizedLeverage = leverage.trim();
  if (config.positionMode !== 'hedging') return `standard-netting-${normalizedLeverage}`;
  const method = config.hedgeMarginMethod ?? 'gross';
  if (method === 'largest_leg') return `standard-hedging-largest-leg-${normalizedLeverage}`;
  if (method === 'offset') {
    const factor = Number(config.hedgeOffsetFactor ?? '0.30');
    const pct = Number.isFinite(factor) ? Math.round(factor * 100) : 30;
    return `standard-hedging-offset-${pct}pct-${normalizedLeverage}`;
  }
  return `standard-hedging-gross-${normalizedLeverage}`;
}

const whole = (raw: string): number | null => (raw.trim() ? Number(raw) : null);
const decimal = (raw: string): string | null => raw.trim() || null;

/**
 * The request the form states. With the request it was loaded from (`base`,
 * from `requestToBuilder`), a term the author did not change is sent exactly
 * as it was loaded — every term the form has no field for included — and only
 * what the author changed is sent from the form. Without one (a brand-new
 * template), the terms the form does not show are the builder's own.
 *
 * @throws Error naming the first rule term that is unset or out of its domain,
 * or a calendar source the steps disagree on.
 */
export function templateRequest(
  payload: CustomChallengeBuilderInput,
  base?: CreateTemplateRequest,
): CreateTemplateRequest {
  const edited = formRequest(payload);
  if (!base) return edited;
  const loaded = requestToBuilder(base, 'edit');
  if (loaded.kind !== 'ready') return edited;
  return overBase(base, formRequest(loaded.form), edited);
}

/** `edited` over `base`: each term `edited` states as `loaded` did (the base,
 *  read into the form and back) is the base's; a changed one is `edited`'s.
 *  Steps are matched by place while their number stands. */
function overBase(
  base: CreateTemplateRequest, loaded: CreateTemplateRequest, edited: CreateTemplateRequest,
): CreateTemplateRequest {
  const merged: Record<string, unknown> = { ...base };
  const from = (key: string, stated: Record<string, unknown>, before: Record<string, unknown>, into: Record<string, unknown>) => {
    if (!sameTerms(stated[key], before[key])) {
      if (stated[key] === undefined) delete into[key];
      else into[key] = stated[key];
    }
  };
  const keys = (...requests: object[]) => new Set(requests.flatMap((request) => Object.keys(request)));
  for (const key of keys(edited, loaded)) {
    if (key !== 'steps') from(key, edited as Record<string, unknown>, loaded as Record<string, unknown>, merged);
  }
  // A phase added or removed moves every later one: the steps are the form's.
  if (edited.steps.length !== loaded.steps.length) {
    merged.steps = edited.steps;
    return merged as CreateTemplateRequest;
  }
  merged.steps = edited.steps.map((step, index) => {
    const [baseStep, loadedStep] = [base.steps[index], loaded.steps[index]];
    if (!baseStep || !loadedStep) return step;
    const into: Record<string, unknown> = { ...baseStep };
    for (const key of keys(step, loadedStep)) {
      from(key, step as Record<string, unknown>, loadedStep as Record<string, unknown>, into);
    }
    return into as CreateStepRequest;
  });
  return merged as CreateTemplateRequest;
}

/** The request the form alone states: the builder's own terms for the rest. */
function formRequest(payload: CustomChallengeBuilderInput): CreateTemplateRequest {
  const steps: CreateStepRequest[] = payload.steps.map((step, index) => {
    // THE TYPED DECLARATION, and nothing beside it: enforcement IS the
    // consequence, and the ladder states it; a family's parameters are its own
    // arm's fields.
    const rules: CreateRuleRequest[] = step.rules
      .filter((rule) => rule.enabled)
      .map((rule) => {
        const refusal = ruleAuthoringRefusal(rule);
        if (refusal) throw new Error(refusal);
        const params = toRuleParams(rule);
        if (!params) throw new Error('Choose all rule terms before saving.');
        return {
          timing: rule.timing as CreateRuleRequest['timing'],
          // OMITTED when the rule measures the whole account, which is what the
          // backend's canonical authoring form omits too — so an account-only
          // template's ruleset hash is unchanged by scopes existing. A group
          // bucket sends its NAME only; the membership travels as `symbol_groups`.
          ...(rule.scope.kind === 'account' ? {} : { scope: wireScope(rule.scope) }),
          params,
          ladder: toLadder(rule),
          warning_threshold_pct: decimal(rule.warningThresholdPct),
        };
      });
    const inactive = step.inactivityDays.trim() !== '';
    return {
      display_name: step.displayName.trim() || `Step ${index + 1}`,
      is_terminal: index === payload.steps.length - 1,
      fresh_account: step.freshAccount,
      profit_target_pct: decimal(step.profitTargetPct),
      profit_target_abs: decimal(step.profitTargetAbs),
      min_trading_days: whole(step.minTradingDays),
      min_profitable_days: whole(step.minProfitableDays),
      time_limit_days: whole(step.timeLimitDays),
      trading_day_mode: step.tradingDayMode,
      min_daily_profit: decimal(step.minDailyProfit),
      consistency_mode: step.consistencyMode || null,
      consistency_pct: decimal(step.consistencyPct),
      consistency_basis: step.consistencyBasis,
      consistency_remedy: step.consistencyRemedy,
      consistency_window: step.consistencyWindow,
      inactivity_days: whole(step.inactivityDays),
      inactivity_clock_from: inactive ? step.inactivityClockFrom : null,
      inactivity_outcome: inactive ? step.inactivityOutcome : null,
      payout_peak_policy: step.payoutPeakPolicy,
      scaling_ladder: step.scalingLadder ?? null,
      // The builder does not author payout policy yet, and the platform
      // requires all three policies in full shape: the neutral ones, stated —
      // the whole available profit, no tiers, automatic approval.
      payout_availability: { max_request_pct_of_available: null, required_remaining_buffer: null },
      payout_tiers: { tiers: [] },
      payout_approval: { mode: 'automatic' },
      challenge_day_boundary_time: step.challengeDayBoundaryTime,
      challenge_day_boundary_timezone: step.challengeDayBoundaryTimezone,
      ...practicePassSettings(payload.steps[index + 1]?.freshAccount),
      rules,
    };
  });

  const calendarSources = new Set(payload.steps.filter((step) => step.rules.some(needsCalendarSource))
    .map((step) => step.calendarSource?.trim() ?? ''));
  if (calendarSources.has('') || calendarSources.size > 1) {
    throw new Error('Calendar rules must name the same explicit source policy in every step.');
  }
  const calendarSource = [...calendarSources][0];
  const symbolGroups = authoredSymbolGroups(payload);
  const hedging = payload.positionMode === 'hedging';

  return {
    ...(calendarSource ? { calendar_source: calendarSource } : {}),
    display_name: payload.displayName.trim(),
    description: payload.description.trim() || null,
    account_size: payload.accountSize.trim(),
    challenge_price: payload.challengePrice.trim(),
    currency: payload.currency,
    leverage: Number(payload.leverage),
    execution_profile_key: executionProfileKey(payload.leverage, {
      positionMode: payload.positionMode,
      hedgeMarginMethod: hedging ? payload.hedgeMarginMethod : undefined,
      hedgeOffsetFactor: hedging && payload.hedgeMarginMethod === 'offset' ? payload.hedgeOffsetFactor : undefined,
    }),
    // The builder's single toggle: unlimited resets under the current rules,
    // or none. A disallowed policy carries no limit and keeps no rules.
    reset_policy: { allowed: payload.resetAllowed, max_resets: null, use_original_rules: false },
    live_limit: NO_LIVE_LIMIT,
    requires_activation: payload.requiresActivation,
    activation_price: payload.requiresActivation ? payload.activationPrice.trim() : null,
    profit_split_pct: decimal(payload.profitSplitPct),
    fee_refund_pct: decimal(payload.feeRefundPct),
    billing_period: payload.billingPeriod,
    failed_account_end: failedAccountEnd(payload),
    template_type: 'practice',
    // OMITTED when the template names no group, so an account-only payload is
    // byte-identical to what it was before group authoring existed.
    ...(symbolGroups.length > 0 ? { symbol_groups: symbolGroups } : {}),
    // Sold terms: stated rather than defaulted server-side, so the choice is
    // visible at the call site.
    settlement_mode: TEMPLATE_SETTLEMENT,
    steps,
  };
}
